'use client'

import { LuMapPin } from 'react-icons/lu'

import { Button } from '@/core/components/form/button/button'
import DragRail from '@/core/components/optional/drag-rail'
import SafeImage from '@/features/veluvit/components/ui/media-image'
import { cn } from '@/core/utils/common'

import {
  CharHead,
  CharLine,
  SceneImage,
  SitLine,
  SysLine,
  UserLine,
} from '@/features/veluvit/components/chat/message-bubbles'
import { openImageViewer } from '@/features/veluvit/components/chat/sheets/image-viewer'
import { fillUser } from '@/features/veluvit/components/reader/reader-blocks'
import DetailBox from '@/features/veluvit/components/detail/detail-box'
import { useCatalogMedia } from '@/features/veluvit/hooks/use-app-data'
import { contentKeys } from '@/features/veluvit/lib/keys'
import { playerName } from '@/features/veluvit/lib/player-name'
import { workFor } from '@/features/veluvit/lib/rules'
import { useSessionStore } from '@/features/veluvit/stores/session.store'
import { useUiStore } from '@/features/veluvit/stores/ui.store'

import type { SeqRow } from '@/features/veluvit/types/catalog.types'

/** 시작 장면 선택 화면도 실제 채팅의 공용 프로필·말풍선·지문을 쓴다. */
export function PreviewRow({
  workId,
  row,
  previous,
}: {
  workId: string
  row: SeqRow
  previous?: SeqRow
}) {
  const { src, thumb } = useCatalogMedia()
  const [type, value, speaker] = row
  if (type === 'img' || type === 'image') {
    const image = type === 'img' ? src(workId, value) : value
    return (
      <SceneImage
        variant="chat"
        src={image}
        fallback={thumb(workId)}
        onOpen={() => openImageViewer(image)}
      />
    )
  }
  if (type === 'sys') return <SysLine text={value} />
  if (type === 'char' || type === 'user' || type === 'sit')
    return (
      <OpeningLine
        workId={workId}
        line={{
          who: type === 'sit' ? 'narration' : type,
          text: value,
          speaker: speaker || undefined,
        }}
        previous={
          previous
            ? {
                who: previous[0] === 'char' ? 'char' : 'narration',
                text: previous[1],
                speaker: previous[2] || undefined,
              }
            : undefined
        }
      />
    )
  return null
}

/** image — 사진 줄(스튜디오 인트로 '사진 넣기'). 서버가 who 'image' 를 받기 전엔 narration 줄에 image 만 얹혀 와서 image 유무로 가른다 */
type StartLine = {
  who: 'char' | 'user' | 'narration' | 'image'
  text: string
  speaker?: string
  speakerId?: string
  image?: string
}
type StartView = {
  key: string
  name: string
  place?: string
  img?: string
  desc?: string
  lines: StartLine[]
  suggestions: string[]
}

function OpeningLine({
  workId,
  line,
  previous,
}: {
  workId: string
  line: StartLine
  previous?: StartLine
}) {
  const sameSpeaker =
    line.speakerId || previous?.speakerId
      ? line.speakerId === previous?.speakerId
      : line.speaker === previous?.speaker
  const head = line.who === 'char' && (previous?.who !== 'char' || !sameSpeaker)
  return (
    <div className="flex w-full min-w-0 flex-col">
      {head && (
        <CharHead
          workId={workId}
          speaker={line.speaker}
          speakerId={line.speakerId}
          overlap
        />
      )}
      {line.who === 'char' ? (
        <CharLine
          workId={workId}
          text={line.text}
          speaker={line.speaker}
          variant="chat"
        />
      ) : line.who === 'user' ? (
        <div className="flex w-full flex-col items-end">
          <UserLine text={line.text} variant="chat" allowDirective={false} />
        </div>
      ) : line.who === 'narration' ? (
        <SitLine text={line.text} variant="chat" />
      ) : line.image ? (
        <SceneImage
          variant="chat"
          src={line.image}
          onOpen={() => {
            if (line.image) openImageViewer(line.image)
          }}
        />
      ) : null}
    </div>
  )
}

/** 미리보기 줄 수 — 넘치면 '더보기' */
const PREVIEW_LINES = 3

/**
 * 시작 상황 — 스튜디오 작품 › 인트로의 시작 설정(Chat.starts: 이름 · 장소 · 인트로 줄 · 추천 답변, 1~5개)을 그대로 보인다.
 * 2026-10-08 스튜디오 기획으로 정리 — 시작이 둘 이상이면 이름 칩으로 골라 보고, 장소 · 인트로 앞 3줄 · 추천 답변을 미리 본다.
 * 숨은 상황(intro.context)은 이용자에게 안 보이는 값이라 서버가 빼고 준다. 서버 시작 설정이 없는 정적 시드만 옛 도입부(intros — 장면 컷 · 대본)로 그린다.
 */
export default function OpeningBox({ id }: { id: string }) {
  const { catalog, src } = useCatalogMedia()
  const c = catalog.chars[id]
  const work = workFor(catalog, id)
  const savedPersonas = useSessionStore((s) => s.data?.personas)
  const userName =
    useUiStore((s) => s.persona[id]?.name) || playerName(savedPersonas)
  const sel = useUiStore((s) => s.selIntro[id] || 0)
  const open = useUiStore((s) => !!s.prevOpen[`${id}:${sel}`])
  const set = useUiStore((s) => s.set)

  const cast = work?.cast || []
  const starts: StartView[] = work?.starts?.length
    ? work.starts.map((s, i): StartView => ({
        key: s.id || `start-${i}`,
        name: s.name || `시작 ${i + 1}`,
        place: s.place,
        lines: (s.intro?.lines || []).map((l) => ({
          who: l.image ? 'image' : l.who,
          text: fillUser(l.text, userName),
          speaker:
            l.who === 'char'
              ? (
                  cast.find((p) => p.id && p.id === l.charId) ??
                  (!l.charId ? cast[0] : undefined)
                )?.name
              : undefined,
          speakerId: l.who === 'char' ? l.charId || cast[0]?.id : undefined,
          image: l.image,
        })),
        suggestions: (s.suggestions || [])
          .filter((t) => typeof t === 'string' && t.trim())
          .slice(0, 3)
          .map((t) => fillUser(t, userName)),
      }))
    : c.intros.map((it, i): StartView => ({
        key: it.id || `${it.title}-${i}`,
        name: it.title,
        place: it.place,
        img: it.img ? src(id, it.img) : undefined,
        desc: it.desc,
        lines: it.seq
          .filter(
            ([type]) => type === 'char' || type === 'user' || type === 'sit'
          )
          .map(([type, value, speaker]): StartLine => ({
            who:
              type === 'char' ? 'char' : type === 'user' ? 'user' : 'narration',
            text: value,
            speaker: speaker || undefined,
          })),
        suggestions: [],
      }))
  const start = starts[sel] || starts[0]
  if (!start) return null
  const rows = open ? start.lines : start.lines.slice(0, PREVIEW_LINES)

  return (
    <DetailBox
      title="시작 상황"
      sub={starts.length > 1 ? `${starts.length}개 중 골라 시작` : undefined}
    >
      {starts.length > 1 && (
        <DragRail
          showArrows={false}
          className="-mx-4 mt-3 over10:mx-0"
          classNames={{ track: 'gap-1.5 px-4 over10:px-0' }}
        >
          {starts.map((it, i) => {
            const on = it.key === start.key
            return (
              <Button
                key={it.key}
                type="button"
                aria-pressed={on}
                onClick={() =>
                  set({
                    selIntro: { ...useUiStore.getState().selIntro, [id]: i },
                  })
                }
                className={cn(
                  // 고른 칩은 흰 채움(주 버튼과 같은 말투) — 코랄 채움은 쓰지 않는다
                  'inline-flex h-8 flex-none cursor-pointer items-center rounded-full px-3.25 text-c13 whitespace-nowrap transition-colors',
                  on
                    ? 'bg-foreground font-bold text-background'
                    : 'bg-foreground/[0.06] text-t2 hover:bg-foreground/[0.1]'
                )}
                variant="bare"
              >
                {it.name}
              </Button>
            )
          })}
        </DragRail>
      )}
      {start.place && (
        <p className="mt-3 flex items-center gap-1 text-c13 text-t3">
          <LuMapPin
            aria-hidden="true"
            className="size-3.5 flex-none text-point"
          />
          {start.place}
        </p>
      )}
      {start.img ? (
        <div className="relative mt-3 aspect-[4/3] overflow-hidden rounded-field bg-black after:absolute after:inset-0 after:bg-[linear-gradient(transparent_40%,rgb(0_0_0/0.92))] after:content-[''] over10:aspect-[16/9]">
          <SafeImage
            src={start.img}
            alt=""
            fill
            sizes="(max-width: 480px) 100vw, 760px"
            className="object-cover object-[50%_22%]"
            appearance="plain"
          />
          {start.desc && (
            <span className="absolute inset-x-3 bottom-2.5 z-[1] text-c12 leading-[1.5] text-t2">
              {start.desc}
            </span>
          )}
        </div>
      ) : (
        start.desc && <p className="mt-2 text-c12 text-t3">{start.desc}</p>
      )}
      {rows.length > 0 && (
        <div className="mt-4 flex min-w-0 flex-col gap-4">
          {contentKeys(
            rows,
            (row) =>
              `${row.who}:${row.speakerId || row.speaker || ''}:${row.image || row.text}`
          ).map(({ key, item }, index) => (
            <OpeningLine
              key={key}
              workId={id}
              line={item}
              previous={rows[index - 1]}
            />
          ))}
          {!open && start.lines.length > PREVIEW_LINES && (
            <Button
              variant="secondary"
              size="sm"
              shape="pill"
              bold
              className="w-full"
              onClick={() =>
                set({
                  prevOpen: {
                    ...useUiStore.getState().prevOpen,
                    [`${id}:${sel}`]: true,
                  },
                })
              }
            >
              +{start.lines.length - PREVIEW_LINES}개 더보기
            </Button>
          )}
        </div>
      )}
      {/* 추천 답변 — 작가가 시작마다 준비한 첫 답(최대 3) */}
      {start.suggestions.length > 0 && (
        <div className="mt-3">
          <p className="text-c12 font-semibold text-t3">추천 답변</p>
          <ul className="mt-1.5 flex list-none flex-wrap gap-1.5 p-0">
            {start.suggestions.map((t) => (
              <li
                key={t}
                className="rounded-full bg-foreground/[0.06] px-3 py-1.5 text-c13 leading-[1.4] break-keep text-t2"
              >
                {t}
              </li>
            ))}
          </ul>
        </div>
      )}
    </DetailBox>
  )
}
